import React, { useEffect, useState } from 'react';
import { Card, Table, Button, Row, Col, Form, Alert, Spinner } from 'react-bootstrap';
import AdminLayout from '../components/AdminLayout';
import api from '../lib/api';
import { Link, useNavigate } from 'react-router-dom';
import { useToast } from '../components/common/ToastProvider';

export default function AdminHandoverPage() {
  const [agents, setAgents] = useState([]);
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('');
  const [agentFilter, setAgentFilter] = useState('');
  const [activityRows, setActivityRows] = useState([]);
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { showToast } = useToast();

  const load = async () => {
    try {
      setLoading(true);
      const [a, q, activity] = await Promise.all([
        api.get('/admin/settings/support-agents/'),
        api.get('/agent/handover/inbox/', { params: agentFilter ? { agent_id: agentFilter } : {} }),
        api.get('/agent/handover/analytics/'),
      ]);
      setAgents(a.data || []);
      const picked = (q?.data?.picked_all || []);
      const pending = (q?.data?.pending || []);
      const cancelled = (q?.data?.cancelled || []);
      const merged = [...picked, ...pending, ...cancelled];
      setItems(status ? merged.filter((h) => h.status === status) : merged);
      setActivityRows(activity?.data?.activity_details || []);
    } catch {
      setMsg('Failed to load handover queue.');
      showToast('Failed to load handover queue.', 'danger');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.post('/handover/notifications/', { mark_all: true }, { suppressToast: true }).catch(() => {});
    load();
  }, [status, agentFilter]);

  const filteredItems = items;

  const pick = async (handoverId, agentId) => {
    try {
      const res = await api.post(`/handover/${handoverId}/pick/`, { agent_id: agentId });
      const handover = res?.data?.handover || {};
      setMsg('Handover picked successfully.');
      showToast('Handover picked successfully.', 'success');
      await load();
      if (handover?.id) {
        navigate(`/admin/handover/${handover.id}`);
      }
    } catch (e) {
      showToast(e?.response?.data?.error || 'Failed to pick handover.', 'danger');
    }
  };

  const resolve = async (handoverId) => {
    try {
      await api.post(`/handover/${handoverId}/resolve/`, {});
      setMsg('Handover resolved successfully.');
      showToast('Handover resolved successfully.', 'success');
      load();
    } catch (e) {
      showToast(e?.response?.data?.error || 'Failed to resolve handover.', 'danger');
    }
  };

  return (
    <AdminLayout
      title="Handover Queue"
      subtitle="Pick live support requests and resolve active handovers."
    >
      {msg ? <Alert variant="success">{msg}</Alert> : null}

      <Card className="admin-surface-card mb-4">
        <Card.Body>
          <div className="admin-section-title">Filter Queue</div>
          <Row className="g-3 align-items-end">
            <Col md={4}>
              <Form.Label>Status</Form.Label>
              <Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">All statuses</option>
                <option value="pending">Pending</option>
                <option value="picked">Picked</option>
                <option value="active">Active</option>
                <option value="cancelled">Cancelled</option>
              </Form.Select>
            </Col>

            <Col md={4}>
              <Form.Label>Agent</Form.Label>
              <Form.Select value={agentFilter} onChange={(e) => setAgentFilter(e.target.value)}>
                <option value="">All agents</option>
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col md={2}>
              <Button onClick={load} className="w-100 admin-action-btn">
                Refresh
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="admin-surface-card">
        <Card.Body>
          <div className="admin-section-title">Live Agent Queue</div>

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="admin-table align-middle mb-0">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Channel</th>
                    <th>Status</th>
                    <th>Requester</th>
                    <th>Agent</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.length ? (
                    filteredItems.map((h) => (
                      <tr key={h.id}>
                        <td>{h.id}</td>
                        <td>{h.channel}</td>
                        <td>{h.status}</td>
                        <td>{h.requester_name || h.requester_email || 'Guest'}</td>
                        <td>{h.picked_by_name || 'Unassigned'}</td>
                        <td>
                          <Form.Select
                            size="sm"
                            className="mb-2"
                            onChange={(e) =>
                              e.target.value && pick(h.id, Number(e.target.value))
                            }
                          >
                            <option value="">Pick as...</option>
                            {agents
                              .filter((a) => a.is_online && a.is_active)
                              .map((a) => (
                                <option key={a.id} value={a.id}>
                                  {a.name}
                                </option>
                              ))}
                          </Form.Select>

                          <Button
                            size="sm"
                            variant="success"
                            className="admin-action-btn"
                            onClick={() => resolve(h.id)}
                          >
                            Resolve
                          </Button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="text-center py-4">
                        No handover items found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      <Card className="admin-surface-card mt-4">
        <Card.Body>
          <div className="admin-section-title">Agent Activity Details</div>
          <div className="text-muted small mb-3">
            Per-agent picked/resolved/replied counts and direct links to recent handover threads.
          </div>
          {activityRows.length ? (
            activityRows.map((row) => (
              <Card key={row?.agent?.id || row?.agent?.email} className="mb-3 border-0" style={{ background: '#f8fafc' }}>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                    <div className="fw-semibold">{row?.agent?.name || 'Agent'} ({row?.agent?.email || 'n/a'})</div>
                    <div className="small text-muted d-flex align-items-center gap-2 flex-wrap">
                      <span>Picked: <strong>{row?.picked_count ?? 0}</strong> · Resolved: <strong>{row?.resolved_count ?? 0}</strong> · Replied: <strong>{row?.replied_count ?? 0}</strong></span>
                      {row?.agent?.id ? <Link to={`/admin/agent-activity/${row.agent.id}`}>View full activity</Link> : null}
                    </div>
                  </div>
                  <div className="table-responsive">
                    <Table size="sm" className="mb-0 align-middle">
                      <thead>
                        <tr>
                          <th>Handover</th>
                          <th>Channel</th>
                          <th>Status</th>
                          <th>Requester</th>
                          <th>Updated</th>
                          <th>Thread</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(row?.timeline || []).length ? (
                          row.timeline.map((item) => (
                            <tr key={`${row?.agent?.id}-${item.handover_id}`}>
                              <td>#{item.handover_id}</td>
                              <td>{item.channel}</td>
                              <td>{item.status}</td>
                              <td>{item.requester_name || item.requester_email || 'Guest'}</td>
                              <td>{item.updated_at ? new Date(item.updated_at).toLocaleString() : '-'}</td>
                              <td><Link to={`/admin/handover/${item.handover_id}`}>Open thread</Link></td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={6} className="text-muted">No recent handover activity.</td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            ))
          ) : (
            <div className="text-muted">No activity details available yet.</div>
          )}
        </Card.Body>
      </Card>
    </AdminLayout>
  );
}
